import { useMemo } from 'react';
import { trpc, Invoice } from 'src/trpc';
import { useToast } from 'components/useToast';
import { Skeleton } from '@/components/ui/skeleton';
import { InvoiceSelectBillingAddressSheet } from './InvoiceSelectBillingAddressSheet';

type Props = {
  invoice: Invoice;
  isLoading?: boolean;
  refetchInvoice: () => void;
};

export const InvoiceBillingAddressSection = ({ invoice, isLoading, refetchInvoice }: Props) => {
  const { successToast, errorToast } = useToast();

  const {
    data: addresses,
    isLoading: isAddressesLoading,
    refetch: refetchAddresses,
  } = trpc.admin.address.adminListUserAddresses.useQuery(invoice.userId, {
    enabled: !!invoice.userId,
  });

  const selectedAddress = useMemo(() => {
    if (!invoice.billingAddressId) return undefined;
    return addresses?.find((a) => a._id === invoice.billingAddressId);
  }, [addresses, invoice.billingAddressId]);

  const { mutate: updateInvoiceBillingAddress, isPending: isUpdatingInvoiceBillingAddress } =
    trpc.admin.invoice.adminUpdateInvoiceBillingAddress.useMutation({
      onSuccess: async () => {
        refetchAddresses();
        successToast({ title: 'Invoice Updated', description: 'Billing address updated.' });
        refetchInvoice();
      },
      onError: () => {
        errorToast({ title: 'Update Failed', description: 'Could not update billing address.' });
      },
    });

  const handleAddressSelect = (addressId: string) => {
    updateInvoiceBillingAddress({ invoiceId: invoice._id, billingAddressId: addressId });
  };

  return (
    <div className="card flex flex-col gap-4 justify-start relative">
      <InvoiceSelectBillingAddressSheet
        isUpdatingInvoice={isUpdatingInvoiceBillingAddress}
        invoice={invoice}
        onSelectAddress={handleAddressSelect}
      />
      <div className="flex flex-col gap-1 w-full">
        <label className="label text-xs">Billing Address</label>
        {isAddressesLoading || isUpdatingInvoiceBillingAddress || isLoading ? (
          <Skeleton className="w-20 h-4" />
        ) : selectedAddress ? (
          <p className="text-text-primary text-sm font-medium">
            {selectedAddress.line1}
            {selectedAddress.line2 ? ` ${selectedAddress.line2}` : ''}, {selectedAddress.city},{' '}
            {selectedAddress.state} {selectedAddress.postalCode}
          </p>
        ) : (
          <p className="text-sm text-slate-600">No billing address selected.</p>
        )}
      </div>
    </div>
  );
};

export default InvoiceBillingAddressSection;
